import {
  type ColumnOrderState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { observeElementRect, useVirtualizer } from "@tanstack/react-virtual";
import { cx } from "class-variance-authority";
import clsx from "clsx";
import { throttle } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Loader } from "@/components/ui/status/Loader/Loader";
import { tableClasses } from "@/components/ui/table/Table";
import type { TableProps } from "@/components/ui/table/TableProps";
import { Typography } from "@/components/ui/text/Typography/Typography";
import { TableUtils } from "@/util/table.utils";

export const VirtualizedTable = <TData,>({
  data,
  columns,
  sorting,
  onSortingChange,
  isLoading,
  className,
  rowClassname,
  tableClassName,
  tableStyle,
  manualSorting,
  onRowDataUsed,
  onRowClick,
  noBorder,
  showAllColumns = false,
  rowHeight = 32,
  flexibleColumnId,
  getRowId,
  stickyRowsData,
}: TableProps<TData>) => {
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);
  const [manuallyToggledColumns, setManuallyToggledColumns] = useState<Set<string>>(new Set());

  // Use refs to avoid dependency loops in callbacks
  const columnVisibilityRef = useRef(columnVisibility);
  const manuallyToggledColumnsRef = useRef(manuallyToggledColumns);

  // Keep refs in sync with state
  useEffect(() => {
    columnVisibilityRef.current = columnVisibility;
  }, [columnVisibility]);

  useEffect(() => {
    manuallyToggledColumnsRef.current = manuallyToggledColumns;
  }, [manuallyToggledColumns]);

  // Reset manual toggle tracking when column order changes
  useEffect(() => {
    setManuallyToggledColumns(new Set());
  }, [columnOrder]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      columnOrder,
    },
    onSortingChange,
    manualSorting,
    manualFiltering: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnVisibilityChange: (updater) => {
      setColumnVisibility(updater);
      // Track which columns have been manually toggled
      if (typeof updater === "function") {
        const newVisibility = updater(columnVisibilityRef.current);
        Object.keys(newVisibility).forEach((columnId) => {
          if (newVisibility[columnId] !== columnVisibilityRef.current[columnId]) {
            setManuallyToggledColumns((prev) => new Set(prev).add(columnId));
          }
        });
      } else {
        Object.keys(updater).forEach((columnId) => {
          if (updater[columnId] !== columnVisibilityRef.current[columnId]) {
            setManuallyToggledColumns((prev) => new Set(prev).add(columnId));
          }
        });
      }
    },
    onColumnOrderChange: setColumnOrder,
    defaultColumn: {
      size: undefined,
    },
  });

  const { t } = useTranslation();

  const { rows } = table.getRowModel();

  // Create a table instance for sticky rows using the same column configuration
  const stickyTable = useReactTable({
    data: stickyRowsData || [],
    columns,
    state: {
      columnVisibility,
      columnOrder,
    },
    manualSorting: true,
    manualFiltering: true,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: () => {}, // Use main table's visibility
    onColumnOrderChange: () => {}, // Use main table's order
    defaultColumn: {
      size: undefined,
    },
  });

  const stickyRows = stickyRowsData && stickyRowsData.length > 0 ? stickyTable.getRowModel().rows : [];

  const parentRef = useRef<HTMLDivElement>(null);

  const calculateVisibleColumns = useCallback(
    (parentWidth: number) => {
      // 👈 NEW: If showAllColumns is true, return all column IDs
      if (showAllColumns) {
        return table.getAllColumns().map((col) => col.id);
      }

      let cumulativeWidth = 0;
      const visibleColumns: string[] = [];
      let allColumns = table.getAllColumns();

      if (table?.getState().columnOrder.length > 0) {
        const orderedColumns = [];
        for (const columnId of table.getState().columnOrder) {
          const column = allColumns.find((col) => col.id === columnId);
          if (column) {
            orderedColumns.push(column);
          }
        }
        // Add any remaining columns that might not be in the order array
        allColumns.forEach((column) => {
          if (!table?.getState().columnOrder.includes(column.id)) {
            orderedColumns.push(column);
          }
        });
        allColumns = orderedColumns;
      }

      const widthBuffer = 15;
      const availableWidth = parentWidth - widthBuffer;

      if (availableWidth <= 0) {
        return allColumns.length > 0 ? [allColumns[0].id] : [];
      }

      const columnWidths = allColumns.map((column) => column.getSize());
      if (columnWidths.every((width) => !width)) {
        return allColumns.map((col) => col.id);
      }

      for (let i = 0; i < allColumns.length; i++) {
        const column = allColumns[i];
        const columnWidth = columnWidths[i] || 0;

        if (!columnWidth) {
          continue;
        }

        if (cumulativeWidth + columnWidth < availableWidth) {
          cumulativeWidth += columnWidth;
          visibleColumns.push(column.id);
        } else {
          break;
        }
      }

      if (visibleColumns.length === 0 && allColumns.length > 0) {
        visibleColumns.push(allColumns[0].id);
      }
      return visibleColumns;
    },
    [table, showAllColumns], // 👈 ADDED showAllColumns TO DEPENDENCIES
  );

  const handleResize = useCallback(
    (parentWidth: number) => {
      // 👈 NEW: Skip resize handling if showAllColumns is true
      if (showAllColumns) {
        const allColumns = table.getAllColumns();
        const newVisibility: Record<string, boolean> = {};
        allColumns.forEach((column) => {
          newVisibility[column.id] = true; // Show all columns
        });
        setColumnVisibility(newVisibility);
        return;
      }

      const allColumns = table.getAllColumns();
      const visibleColumnIds = calculateVisibleColumns(parentWidth);
      const newVisibility: Record<string, boolean> = {};

      allColumns.forEach((column) => {
        // If column was manually toggled, preserve its current visibility
        if (manuallyToggledColumnsRef.current.has(column.id)) {
          newVisibility[column.id] = columnVisibilityRef.current[column.id] ?? true;
        } else {
          // Otherwise, use automatic width-based visibility
          newVisibility[column.id] = visibleColumnIds.includes(column.id);
        }
      });

      setColumnVisibility(newVisibility);
    },
    [table, calculateVisibleColumns, showAllColumns],
  );

  const throttledHandleResize = useMemo(() => throttle(handleResize, 30), [handleResize]);

  useEffect(() => {
    const timer = setTimeout(() => {
      throttledHandleResize(parentRef.current?.clientWidth ?? 0);
    }, 100);

    return () => clearTimeout(timer);
  }, [throttledHandleResize]);

  useEffect(() => {
    const handleWindowResize = () => {
      throttledHandleResize(parentRef.current?.clientWidth ?? 0);
    };

    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, [throttledHandleResize]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    observeElementRect: (instance, cb) => {
      observeElementRect(instance, (rect) => {
        cb(rect);
        throttledHandleResize(rect.width);
      });
    },
  });

  return (
    <div
      data-virtual-table="container"
      className={clsx(
        "flex w-full flex-[1_1_0] flex-col",
        tableClasses.table,
        tableClassName,
        noBorder && "!rounded-none! border-0",
      )}
      style={tableStyle}
    >
      {table.getHeaderGroups().map((headerGroup) => (
        <div
          data-virtual-table="header"
          key={headerGroup.id}
          className={cx(tableClasses.tableHeader, flexibleColumnId && "flex")}
        >
          {headerGroup.headers.map((header) => {
            const isFlexibleColumn = TableUtils.isColumnFlexible(header.column, flexibleColumnId);
            return (
              <div
                key={header.id}
                style={{
                  width: TableUtils.getColumnWidth(header.column, isFlexibleColumn),
                }}
                className={clsx(isFlexibleColumn ? "min-w-0 flex-1" : "inline-block", tableClasses.tableCell)}
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
              </div>
            );
          })}
        </div>
      ))}
      <div
        data-virtual-table="scroll-pane"
        ref={parentRef}
        className="flex-[1_1_0] gap-3 overflow-y-auto"
      >
        {stickyRows.length > 0 && (
          <div className="sticky top-0 z-10 bg-secondary-400">
            {stickyRows.map((row) => (
              <div
                key={row.id}
                onClick={() => onRowClick?.(row.original)}
                className={cx(
                  "cursor-pointer",
                  rowClassname?.(row.original),
                  tableClasses.tableRow,
                  flexibleColumnId && "flex",
                )}
                style={{
                  height: `${rowHeight}px`,
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  const isFlexibleColumn = TableUtils.isColumnFlexible(cell.column, flexibleColumnId);
                  return (
                    <div
                      className={clsx(isFlexibleColumn ? "min-w-0 flex-1" : "inline-block")}
                      key={cell.id}
                      style={{
                        width: TableUtils.getColumnWidth(cell.column, isFlexibleColumn),
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
        <div
          data-virtual-table="rows"
          className={clsx("overflow-y-hidden", className)}
          style={{ height: `${virtualizer.getTotalSize()}px` }}
        >
          {!isLoading &&
            virtualizer.getVirtualItems().map((virtualRow, rowIndex) => {
              const row = rows[virtualRow.index];
              onRowDataUsed?.(row.original);
              const rowId = getRowId?.(row.original, virtualRow.index);
              return (
                <div
                  id={rowId}
                  key={row.id}
                  onClick={() => onRowClick?.(row.original)}
                  className={cx(
                    "cursor-pointer",
                    rowClassname?.(row.original),
                    tableClasses.tableRow,
                    flexibleColumnId && "flex",
                  )}
                  style={{
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start - rowIndex * virtualRow.size}px)`,
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    const isFlexibleColumn = TableUtils.isColumnFlexible(cell.column, flexibleColumnId);

                    return (
                      <div
                        className={clsx(isFlexibleColumn ? "min-w-0 flex-1" : "inline-block", "py-2")}
                        key={cell.id}
                        style={{
                          width: TableUtils.getColumnWidth(cell.column, isFlexibleColumn),
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          {!isLoading && table.getRowModel().rows.length === 0 && (
            <div>
              <Typography
                size="body-paragraph-m"
                variant="default"
                className="text-white"
              >
                {t("table.noData")}
              </Typography>
            </div>
          )}
          {isLoading && (
            <div className={tableClasses.tableLoading}>
              <Loader size="l" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
